import { useCallback, useMemo, useState } from 'react';
import { Button } from '@/shared/components';
import { PermissionGuard } from '@/features/auth';
import type { ConfigOptionRecord } from '../services/smr-admin';
import type { Product, ConfigSetting } from '@/features/products/types/products.types';
import { useConfigSettingMutations } from '../hooks/useConfigSettingMutations';
import { ConfigSettingDataTable } from './ConfigSettingDataTable';
import { ConfigSettingAddForm } from './ConfigSettingAddForm';
import { ApplyCatalogModal } from './ApplyCatalogModal';
import {
  configuredSchedulingMethods,
  inapplicableSettingNote,
  isOptionApplicable,
  schedulingMethodLabeller,
} from '../utils/schedulingMethodRequirement';

interface Props { productId: string; product: Product; }

export function ProductConfigSettingPanel({ productId, product }: Props) {
  const [showForm, setShowForm] = useState(false);
  const [showApply, setShowApply] = useState(false);
  const mutations = useConfigSettingMutations(productId);

  const settings: ConfigSetting[] = useMemo(() => product.config_settings ?? [], [product.config_settings]);
  const methods = useMemo(() => configuredSchedulingMethods(settings), [settings]);
  const methodLabel = useMemo(() => schedulingMethodLabeller(settings), [settings]);
  const isOptionAvailable = useCallback(
    (option: ConfigOptionRecord) => isOptionApplicable(option, methods),
    [methods],
  );
  const settingNote = useCallback(
    (setting: ConfigSetting) => inapplicableSettingNote(setting, methods, methodLabel),
    [methods, methodLabel],
  );

  // A soft-deleted (retired) product's config-setting routes 404 on mutation, and
  // ProductDetail already hides Edit for it — keep this tab consistent by making it
  // view-only: no Add/Apply controls and no per-row edit/delete actions.
  const isRetired = Boolean(product.deleted_at);

  // Hide options already applied to this product from the add form, so a
  // single-valued key cannot be added twice. Multi-valued options (today:
  // `features`) stay available -- the catalogue says which, via
  // ConfigOptionRecord.is_multi_valued (#735). An unhydrated configOption falls
  // through to "treat as single-valued", which matches the column default and is
  // the safe direction; ProductController::show() eager-loads
  // config_settings.configOption, so it is hydrated on this screen.
  const appliedOptionIds = settings
    .filter((s) => !s.configOption?.is_multi_valued && s.config_option_id)
    .map((s) => s.config_option_id as string);

  return (
    <section className="space-y-6">
      {settings.length === 0 ? (
        <p className="text-mp-muted text-sm">No config settings.</p>
      ) : (
        <ConfigSettingDataTable
          data={settings}
          mutations={mutations}
          readOnly={isRetired}
          settingNote={settingNote}
        />
      )}

      {!isRetired && (
        <div className="border-t border-mp-border pt-6 max-w-2xl">
          <h3 className="text-lg font-semibold mb-2">Config Settings</h3>
          {showForm ? (
            <PermissionGuard permission={{ resource: 'smr', action: 'product.edit' }}>
              <ConfigSettingAddForm
                mutations={mutations}
                onDone={() => setShowForm(false)}
                excludeOptionIds={appliedOptionIds}
                isOptionAvailable={isOptionAvailable}
              />
            </PermissionGuard>
          ) : (
            <div className="flex items-start gap-4 justify-between">
              <p className="text-sm text-mp-muted">
                Configuration values that apply to this product, grouped by category.
              </p>
              <PermissionGuard permission={{ resource: 'smr', action: 'product.edit' }}>
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <Button size="sm" variant="secondary" onClick={() => setShowApply(true)}>
                    Apply catalog group
                  </Button>
                  <Button size="sm" onClick={() => setShowForm(true)}>
                    Add Setting
                  </Button>
                </div>
              </PermissionGuard>
            </div>
          )}
        </div>
      )}

      {!isRetired && showApply && (
        <ApplyCatalogModal
          productId={productId}
          existingKeys={settings.map((s) => s.key)}
          mutations={mutations}
          onClose={() => setShowApply(false)}
          isOptionAvailable={isOptionAvailable}
        />
      )}
    </section>
  );
}
